/**
 * Stop-centric poster data assembly (Printable Route Timetable, milestone M1).
 *
 * Builds the "all services calling at this stop, grouped by day type" view that a
 * UK at-stop timetable poster renders — from local fixed-route primitives
 * (`tripSchedules` + `serviceDays`), entirely client-side.
 *
 * Note: `summarizeFixedRouteStopServices` (src/utils/transitStopService.ts) is a
 * display-only summary (truncated to 5 times, no day-type split) and is NOT reused
 * here — this is the fuller assembly.
 *
 * ponytail: fixed-route source only for M1. A fixed route carries a single
 * ServiceDaysMask, so every trip shares one day-type group; true multi-service
 * (weekday/Sat/Sun) day-type splitting lands with the hosted-GTFS source (M3).
 */
import type { FixedRoute, FixedRouteStop, ServiceDaysMask } from '../../types/fixedRoutes';
import { generateTimetable } from './timetableUtils';
import { DEFAULT_OPERATING_HOURS } from '../../config/constants';

export interface PosterDayGroup {
  /** e.g. "Monday to Friday", "Saturday only", "Daily". */
  label: string;
  /** Departure times at this stop, 24h "HH:MM", sorted ascending. */
  times: string[];
}

export interface PosterColumn {
  routeId: string;
  /** GTFS route_short_name — the public service number (e.g. "300"). */
  serviceNumber?: string;
  /** Operating agency name, when known. */
  operator?: string;
  /** Route colour (hex), for the Venice line badge / strip. */
  routeColor?: string;
  /** e.g. "to Carlisle". */
  destinationLabel: string;
  /** Stop names from the selected stop (index 0 = "you are here") to the terminus. */
  onwardStops: string[];
  dayGroups: PosterDayGroup[];
}

export interface StopPosterData {
  stopId: string;
  stopName: string;
  atcoCode?: string;
  naptanCode?: string;
  /** One column per route serving the stop (with at least one departure). */
  columns: PosterColumn[];
}

const DAY_NAMES = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

/** Human day-type label from a Mon–Sun service mask (mirrors UK poster headers). */
export function dayTypeLabel(mask?: ServiceDaysMask): string {
  if (!mask) return 'Daily';
  const flags = [
    mask.monday,
    mask.tuesday,
    mask.wednesday,
    mask.thursday,
    mask.friday,
    mask.saturday,
    mask.sunday,
  ];
  const active = flags.map((on, i) => (on ? i : -1)).filter((i) => i >= 0);
  if (active.length === 0) return 'Not in service';
  if (active.length === 7) return 'Daily';
  if (active.length === 2 && active[0] === 5 && active[1] === 6) return 'Weekends';
  const contiguous = active.every((v, i, arr) => i === 0 || v === arr[i - 1] + 1);
  if (contiguous) {
    return active.length === 1
      ? `${DAY_NAMES[active[0]]} only`
      : `${DAY_NAMES[active[0]]} to ${DAY_NAMES[active[active.length - 1]]}`;
  }
  return active.map((i) => DAY_NAMES[i].slice(0, 3)).join(', ');
}

/** Minutes-from-midnight (may exceed 1439 for overnight) → 24h "HH:MM". */
export function formatMinutes(totalMinutes: number): string {
  const n = ((Math.round(totalMinutes) % 1440) + 1440) % 1440;
  const h = Math.floor(n / 60);
  const m = n % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function hhmmToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

export interface HourGrid {
  /** Contiguous "HH" columns from the earliest to latest departure hour. */
  hours: string[];
  /** Row-major minutes ("MM") under each hour column; null where that hour has no more. */
  rows: Array<Array<string | null>>;
}

/**
 * Pivot a flat list of "HH:MM" departures into the Venice-style hour grid:
 * one column per hour (earliest→latest present), minutes stacked beneath.
 */
export function buildHourGrid(times: string[]): HourGrid {
  const byHour = new Map<string, string[]>();
  for (const t of times) {
    const [h, m] = t.split(':');
    const arr = byHour.get(h) ?? [];
    arr.push(m);
    byHour.set(h, arr);
  }
  if (byHour.size === 0) return { hours: [], rows: [] };
  const present = Array.from(byHour.keys()).map(Number);
  const min = Math.min(...present);
  const max = Math.max(...present);
  const hours: string[] = [];
  for (let h = min; h <= max; h++) hours.push(String(h).padStart(2, '0'));
  const maxRows = Math.max(...hours.map((h) => byHour.get(h)?.length ?? 0));
  const rows: Array<Array<string | null>> = [];
  for (let r = 0; r < maxRows; r++) {
    rows.push(hours.map((h) => (byHour.get(h) ?? [])[r] ?? null));
  }
  return { hours, rows };
}

/**
 * Departure times (24h "HH:MM") at a stop for one route, sorted & de-duplicated.
 *
 * Prefers explicit GTFS `tripSchedules`; otherwise falls back to the synthesized
 * timetable (`generateTimetable`) — the same source the Timetable timeline shows —
 * so hand-authored / shuttle routes (which have no `tripSchedules`) still produce
 * departures. ponytail: when falling back, a route with `reverseReturnJourney`
 * mixes both directions' times into one column; acceptable for M1.
 */
function departuresAtStop(route: FixedRoute, stopIndex: number, stops: FixedRouteStop[]): string[] {
  if (route.tripSchedules && route.tripSchedules.length > 0) {
    const rawMinutes = route.tripSchedules
      .map((trip) => {
        const offset = trip.stopArrivalTimesMinutes[stopIndex];
        return typeof offset === 'number' ? trip.startTimeMinutes + offset : null;
      })
      .filter((v): v is number => v !== null);
    // dedupe on raw minutes so 01:30 and 25:30 stay distinct
    return Array.from(new Set(rawMinutes))
      .sort((a, b) => a - b)
      .map(formatMinutes);
  }

  // Fallback: synthesize from start time + segments (matches the timeline).
  const stopId = route.stops[stopIndex].stopId;
  const stopNames = new Map(stops.map((s) => [s.id, s.name]));
  const { timetableEntries } = generateTimetable(
    route,
    route.trafficMultiplier ?? 1,
    stopNames,
    DEFAULT_OPERATING_HOURS.startHour,
    DEFAULT_OPERATING_HOURS.endHour
  );
  const times = timetableEntries
    .filter((entry) => entry.stopId === stopId)
    .map((entry) => entry.departureTime);
  return Array.from(new Set(times)).sort((a, b) => hhmmToMinutes(a) - hhmmToMinutes(b));
}

/**
 * Assemble poster data for one stop across every route that serves it.
 * `selectedRoutes` is typically the whole fixed-route set so all services at the
 * stop appear, not only the route the operator started from.
 */
export function buildStopPosterData(
  stopId: string,
  selectedRoutes: FixedRoute[],
  stops: FixedRouteStop[]
): StopPosterData {
  const stop = stops.find((s) => s.id === stopId);
  const columns: PosterColumn[] = [];

  for (const route of selectedRoutes) {
    if (route.hidden) continue;
    const stopIndex = route.stops.findIndex((sc) => sc.stopId === stopId);
    if (stopIndex < 0) continue;

    const times = departuresAtStop(route, stopIndex, stops);
    if (times.length === 0) continue;

    const lastStopId = route.stops[route.stops.length - 1]?.stopId;
    const destinationName =
      route.tripHeadsign ||
      (lastStopId && lastStopId !== stopId
        ? stops.find((s) => s.id === lastStopId)?.name ?? lastStopId
        : route.name);

    // Onward stop names from the selected stop (inclusive) to the terminus.
    const onwardStops = route.stops
      .slice(stopIndex)
      .map((sc) => stops.find((s) => s.id === sc.stopId)?.name ?? sc.stopId);

    columns.push({
      routeId: route.id,
      serviceNumber: route.shortName,
      operator: route.gtfsAgency?.name,
      routeColor: route.color,
      destinationLabel: `to ${destinationName}`,
      onwardStops,
      dayGroups: [{ label: dayTypeLabel(route.serviceDays), times }],
    });
  }

  return {
    stopId,
    stopName: stop?.name ?? stopId,
    atcoCode: stop?.atcoCode,
    naptanCode: stop?.naptanCode,
    columns,
  };
}
